import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Boxes, ClipboardList, PackageCheck, Truck } from 'lucide-react';
import {
  Column,
  DataTable,
  KPIGrid,
  LoadingState,
  MetricCard,
  PageHeader,
  TableFilters,
} from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  portalApi,
  type PortalCallOff,
  type PortalInbound,
  type PortalPositionLine,
  type PortalSite,
  type PortalPerformance,
  type PortalSummary,
} from '@/api/services';
import { fadeUp, stagger } from '@/lib/animations';
import { cn } from '@/lib/utils';

/**
 * What a customer sees of the stock their supplier holds at their sites.
 *
 * Written from the customer's side of the arrangement, which changes every
 * label: the internal screens say "ours" and "theirs", and here the same two
 * numbers are "still the supplier's" and "yours". Same data, opposite vantage
 * point, and getting the words the wrong way round would be worse than showing
 * nothing.
 *
 * There is no money on this page at all. Not hidden behind a permission — simply
 * not in the payload, because what the supplier paid is not the customer's
 * business and the safest place for a figure that must not cross is nowhere near
 * the wire.
 */

const STATUS_TONE: Record<string, string> = {
  REQUESTED: 'bg-status-warning-subtle text-status-warning-foreground',
  APPROVED: 'bg-status-info-subtle text-status-info-foreground',
  FULFILLED: 'bg-status-info-subtle text-status-info-foreground',
  COLLECTED: 'bg-status-success-subtle text-status-success-foreground',
  REJECTED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  CANCELLED: 'bg-muted text-muted-foreground',
};

/** The same states, described by what they mean for the customer. */
const STATUS_LABEL: Record<string, string> = {
  REQUESTED: 'With your supplier',
  APPROVED: 'Approved',
  FULFILLED: 'Yours — awaiting collection',
  COLLECTED: 'Collected',
  REJECTED: 'Declined',
  CANCELLED: 'Withdrawn',
};

export default function Portal() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('position');
  const [search, setSearch] = useState('');

  const [summary, setSummary] = useState<PortalSummary | null>(null);
  const [sites, setSites] = useState<PortalSite[]>([]);
  const [callOffs, setCallOffs] = useState<PortalCallOff[]>([]);
  const [inbound, setInbound] = useState<PortalInbound[]>([]);
  const [performance, setPerformance] = useState<PortalPerformance | null>(null);

  const [siteId, setSiteId] = useState<string | null>(null);
  const [draw, setDraw] = useState<Record<string, string>>({});
  const [raising, setRaising] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p, c, i, perf] = await Promise.all([
        portalApi.summary(),
        portalApi.positions(),
        portalApi.callOffs(),
        portalApi.inbound(),
        portalApi.performance(),
      ]);
      setPerformance(perf);
      setSummary(s);
      setSites(p.sites);
      setCallOffs(c);
      setInbound(i);
      setSiteId((prev) => prev ?? p.sites[0]?.id ?? null);
    } catch {
      toast.error('Could not load your position');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const site = useMemo(() => sites.find((s) => s.id === siteId) ?? null, [sites, siteId]);

  const matches = (haystack: string[]) =>
    !search || haystack.some((h) => h?.toLowerCase().includes(search.toLowerCase()));

  const raise = async () => {
    if (!site) return;
    const lines = Object.entries(draw)
      .map(([itemId, qty]) => ({ itemId, quantity: Number(qty) }))
      .filter((l) => Number.isInteger(l.quantity) && l.quantity > 0);
    if (!lines.length) return;

    setRaising(true);
    try {
      const created = await portalApi.raiseCallOff({ locationId: site.id, lines });
      toast.success(`${created.reference} sent to your supplier`);
      setDraw({});
      setTab('call-offs');
      await load();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not send that request';
      toast.error(message);
    } finally {
      setRaising(false);
    }
  };

  const drawnCount = Object.values(draw).filter((v) => Number(v) > 0).length;

  const positionColumns: Column<PortalPositionLine>[] = [
    {
      key: 'sku',
      header: 'Item',
      sortable: true,
      render: (l) => (
        <div>
          <div className="font-medium">{l.sku}</div>
          <div className="text-xs text-muted-foreground">{l.name}</div>
        </div>
      ),
    },
    {
      key: 'onSite',
      header: 'On site',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) => `${l.onSite}${l.unit ? ` ${l.unit}` : ''}`,
    },
    {
      key: 'availableToDraw',
      header: 'Available to draw',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) => <span className="font-medium">{l.availableToDraw}</span>,
    },
    {
      key: 'yours',
      header: 'Already yours',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) =>
        l.yours > 0 ? (
          <span className="font-medium text-status-info-foreground">{l.yours}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'agreedMin',
      header: 'Agreed minimum',
      className: 'text-right tabular-nums',
      accessorFn: (l) => l.agreedMin ?? -1,
      render: (l) =>
        // Only a minimum somebody actually agreed. Showing the counterparty a
        // number nobody set would put a contractual-looking figure in front of
        // the one person entitled to hold the supplier to it.
        l.agreedMin == null ? (
          <span className="text-muted-foreground">none agreed</span>
        ) : (
          <span className={cn(l.onSite < l.agreedMin && 'text-status-warning-foreground')}>
            {l.agreedMin}
          </span>
        ),
    },
    ...(summary?.canRaiseCallOffs
      ? [
          {
            key: 'draw',
            header: 'Request',
            className: 'w-32',
            render: (l: PortalPositionLine) => (
              <Input
                type="number"
                min={0}
                max={l.availableToDraw}
                placeholder="0"
                className="h-8"
                disabled={l.availableToDraw <= 0}
                value={draw[l.itemId] ?? ''}
                onChange={(e) => setDraw((d) => ({ ...d, [l.itemId]: e.target.value }))}
              />
            ),
          } as Column<PortalPositionLine>,
        ]
      : []),
  ];

  const callOffColumns: Column<PortalCallOff>[] = [
    { key: 'reference', header: 'Reference', sortable: true },
    {
      key: 'location',
      header: 'Site',
      accessorFn: (c) => c.location.code,
      render: (c) => c.location.code,
    },
    {
      key: 'lines',
      header: 'Items',
      accessorFn: (c) => c.lines.length,
      render: (c) => (
        <div className="space-y-0.5">
          {c.lines.slice(0, 2).map((l) => (
            <div key={l.id} className="text-xs">
              <span className="font-mono">{l.item.sku}</span>{' '}
              <span className="text-muted-foreground">× {l.quantityRequested}</span>
            </div>
          ))}
          {c.lines.length > 2 && (
            <div className="text-xs text-muted-foreground">+{c.lines.length - 2} more</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <Badge className={cn('font-normal', STATUS_TONE[c.status])}>
          {STATUS_LABEL[c.status] ?? c.status}
        </Badge>
      ),
    },
    {
      key: 'requestedAt',
      header: 'Requested',
      sortable: true,
      render: (c) => new Date(c.requestedAt).toLocaleDateString(),
    },
  ];

  const inboundColumns: Column<PortalInbound>[] = [
    { key: 'shipmentNumber', header: 'Shipment', sortable: true },
    {
      key: 'destination',
      header: 'To',
      accessorFn: (s) => s.destination.code,
      render: (s) => s.destination.code,
    },
    {
      key: 'items',
      header: 'Contents',
      accessorFn: (s) => s.items.length,
      render: (s) => (
        <div className="space-y-0.5">
          {s.items.slice(0, 2).map((it, i) => (
            <div key={i} className="text-xs">
              <span className="font-mono">{it.item.sku}</span>{' '}
              <span className="text-muted-foreground">× {it.quantity}</span>
            </div>
          ))}
          {s.items.length > 2 && (
            <div className="text-xs text-muted-foreground">+{s.items.length - 2} more</div>
          )}
        </div>
      ),
    },
    { key: 'status', header: 'Status', sortable: true },
    {
      key: 'estimatedArrival',
      header: 'Expected',
      sortable: true,
      render: (s) =>
        // Blank when the supplier has not given one. A date invented here would
        // be a promise made on their behalf.
        s.estimatedArrival ? (
          new Date(s.estimatedArrival).toLocaleDateString()
        ) : (
          <span className="text-muted-foreground">not given</span>
        ),
    },
  ];

  if (loading) return <LoadingState />;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Your consignment stock"
          description={
            summary?.suppliers.length
              ? `Material held at your sites by ${summary.suppliers.join(', ')} under contract.`
              : 'Material held at your sites under a consignment contract.'
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Your sites"
            value={summary?.sites ?? 0}
            subtitle={`${summary?.skus ?? 0} items stocked`}
            icon={<Boxes className="h-4 w-4" />}
          />
          <MetricCard
            label="Available to draw"
            value={summary?.ourStockOnSite ?? 0}
            subtitle="held for you, not yet drawn"
            icon={<PackageCheck className="h-4 w-4" />}
          />
          <MetricCard
            label="Already yours"
            value={summary?.awaitingCollection ?? 0}
            subtitle="drawn and awaiting collection"
            icon={<ClipboardList className="h-4 w-4" />}
          />
          <MetricCard
            label="On its way"
            value={inbound.length}
            subtitle="shipments in transit to your sites"
            icon={<Truck className="h-4 w-4" />}
          />
        </KPIGrid>
      </motion.div>

      {performance && (
        <motion.div variants={fadeUp}>
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Your contract
                </p>
                <p className="mt-1 text-sm">
                  {performance.supplier}
                  {performance.contractReference && (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {performance.contractReference}
                    </span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Agreed availability
                  </p>
                  <p className="text-xl font-semibold tabular-nums">
                    {performance.target == null
                      ? 'none agreed'
                      : `${(performance.target * 100).toFixed(1)}%`}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Actual
                  </p>
                  <p
                    className={cn(
                      'text-xl font-semibold tabular-nums',
                      performance.verdict === 'MISSED' && 'text-status-destructive-foreground',
                      performance.verdict === 'MET' && 'text-status-success-foreground'
                    )}
                  >
                    {performance.availability == null
                      ? 'not measured'
                      : `${(performance.availability * 100).toFixed(1)}%`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {/* The window, on the figure. A number over three days is a
                        different claim from one over ninety. */}
                    over {performance.daysObserved} recorded{' '}
                    {performance.daysObserved === 1 ? 'day' : 'days'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="position">Position</TabsTrigger>
            <TabsTrigger value="call-offs">Requests ({callOffs.length})</TabsTrigger>
            <TabsTrigger value="inbound">Inbound ({inbound.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="position" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Site</span>
                <Select value={siteId ?? undefined} onValueChange={setSiteId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Choose a site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} — {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {summary?.canRaiseCallOffs && (
                <Button onClick={raise} disabled={raising || drawnCount === 0}>
                  {raising
                    ? 'Sending…'
                    : drawnCount === 0
                      ? 'Request stock'
                      : `Request ${drawnCount} item${drawnCount === 1 ? '' : 's'}`}
                </Button>
              )}
            </div>

            <DataTable<PortalPositionLine>
              data={(site?.lines ?? []).filter((l) => matches([l.sku, l.name]))}
              columns={positionColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by item…"
                />
              }
              pagination
              exportable
              emptyTitle="Nothing held at this site"
              emptyDescription="Your supplier has not recorded any stock against this site yet."
            />

            <p className="text-xs text-muted-foreground">
              <span className="font-medium">On site</span> is everything standing there.{' '}
              <span className="font-medium">Available to draw</span> is what is still your
              supplier&rsquo;s and free to request. <span className="font-medium">Already yours</span>{' '}
              has been drawn — title has passed and it is waiting to be collected.
              {!summary?.canRaiseCallOffs && (
                <>
                  {' '}
                  This account can view the position but not request stock; your supplier controls
                  that.
                </>
              )}
            </p>
          </TabsContent>

          <TabsContent value="call-offs" className="mt-4">
            <DataTable<PortalCallOff>
              data={callOffs.filter((c) => matches([c.reference, c.location.code]))}
              columns={callOffColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by reference or site…"
                />
              }
              pagination
              exportable
              emptyTitle="No requests yet"
              emptyDescription="Requests you raise against stock at your sites appear here, with where each one has got to."
            />
          </TabsContent>

          <TabsContent value="inbound" className="mt-4">
            <DataTable<PortalInbound>
              data={inbound.filter((s) => matches([s.shipmentNumber, s.destination.code]))}
              columns={inboundColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by shipment or site…"
                />
              }
              pagination
              exportable
              emptyTitle="Nothing in transit"
              emptyDescription="Shipments your supplier has despatched to your sites appear here until they arrive."
            />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
